/* fswond — drawer base
 * Uso: <fs-drawer name="cart">…</fs-drawer>
 * Apri: window.FsEvents.emit('drawer:open', { name: 'cart' });
 */
class FsDrawer extends HTMLElement {
  connectedCallback() {
    this.name = this.getAttribute('name');
    this._onKeydown = this._onKeydown.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);

    this._backdrop = this.querySelector('.fs-drawer__backdrop');
    this._closeBtns = this.querySelectorAll('[data-drawer-close]');

    if (this._backdrop) this._backdrop.addEventListener('click', this._onBackdropClick);
    this._closeBtns.forEach((b) => b.addEventListener('click', this._onCloseClick));

    if (window.FsEvents) {
      this._unsubOpen = window.FsEvents.on('drawer:open', (detail = {}) => {
        const { name } = detail;
        if (name === this.name) this.open();
      });
      this._unsubClose = window.FsEvents.on('drawer:close', (detail = {}) => {
        const { name } = detail;
        if (!name || name === this.name) this.close();
      });
    }
  }

  disconnectedCallback() {
    if (this._unsubOpen) this._unsubOpen();
    if (this._unsubClose) this._unsubClose();
    document.removeEventListener('keydown', this._onKeydown);
  }

  open() {
    this._previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Close any other open drawer
    document.querySelectorAll('fs-drawer.is-open').forEach((d) => { if (d !== this) d.close(); });
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('fs-no-scroll');
    document.addEventListener('keydown', this._onKeydown);

    // focus trap entry
    const firstFocus = this.querySelector('[data-autofocus], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    window.requestAnimationFrame(() => {
      if (firstFocus) firstFocus.focus({ preventScroll: true });
    });
    this.dispatchEvent(new CustomEvent('fs-drawer:opened', { bubbles: true }));
  }

  close() {
    const wasOpen = this.classList.contains('is-open');
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('fs-drawer.is-open')) {
      document.documentElement.classList.remove('fs-no-scroll');
    }
    document.removeEventListener('keydown', this._onKeydown);

    if (wasOpen && this._previousActiveElement && document.contains(this._previousActiveElement)) {
      this._previousActiveElement.focus({ preventScroll: true });
    }
    this.dispatchEvent(new CustomEvent('fs-drawer:closed', { bubbles: true }));
  }

  _onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); this.close(); }
    if (e.key === 'Tab') this._trapFocus(e);
  }

  _onBackdropClick() { this.close(); }
  _onCloseClick(e) { e.preventDefault(); this.close(); }

  _trapFocus(e) {
    const focusables = this.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}

if (!customElements.get('fs-drawer')) customElements.define('fs-drawer', FsDrawer);
